/**
 * Filter Group Editor
 *
 * Manages a group of filter rules with AND/OR logic
 */

import React from 'react';
import { Badge } from '../ui/badge.tsx';
import { Plus, Trash2 } from 'lucide-react';
import type { FilterGroup, FilterRule } from '../../types/selectionV2.ts';
import { createFilterRule } from '../../types/selectionV2.ts';
import FilterRuleEditor from './FilterRuleEditor.tsx';

interface FilterGroupEditorProps {
  group: FilterGroup;
  onChange: (group: FilterGroup) => void;
  onRemove: () => void;
  formulas?: Array<{ id: string; name: string; formula: string }>;
}

const FilterGroupEditor: React.FC<FilterGroupEditorProps> = ({
  group,
  onChange,
  onRemove,
  formulas = []
}) => {
  const handleAddRule = () => {
    const newRule = createFilterRule();
    onChange({
      ...group,
      rules: [...group.rules, newRule]
    });
  };

  const handleUpdateRule = (ruleId: string, updated: FilterRule) => {
    onChange({
      ...group,
      rules: group.rules.map(r => r.id === ruleId ? updated : r)
    });
  };

  const handleRemoveRule = (ruleId: string) => {
    onChange({
      ...group,
      rules: group.rules.filter(r => r.id !== ruleId)
    });
  };

  const handleToggleMatch = () => {
    onChange({
      ...group,
      match: group.match === 'ALL' ? 'ANY' : 'ALL'
    });
  };

  return (
    <div className="border-2 border-gray-300 rounded-lg p-4 bg-white">
      {/* Group Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Match:</span>
          <button
            onClick={handleToggleMatch}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              group.match === 'ALL'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            ALL (AND)
          </button>
          <button
            onClick={handleToggleMatch}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              group.match === 'ANY'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            ANY (OR)
          </button>
        </div>
        <button
          onClick={onRemove}
          className="p-2 text-red-500 hover:bg-red-50 rounded-md transition-colors"
          title="Remove group"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Rules */}
      <div className="space-y-3">
        {group.rules.length === 0 ? (
          <div className="text-center py-4 bg-gray-50 rounded border-2 border-dashed border-gray-300">
            <p className="text-sm text-gray-500">No rules in this group</p>
          </div>
        ) : (
          group.rules.map((rule, index) => (
            <div key={rule.id}>
              {index > 0 && (
                <div className="flex items-center justify-center py-1">
                  <Badge variant="secondary" className={
                    group.match === 'ALL' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                  }>
                    {group.match}
                  </Badge>
                </div>
              )}
              <FilterRuleEditor
                rule={rule}
                onChange={(updated) => handleUpdateRule(rule.id, updated)}
                onRemove={() => handleRemoveRule(rule.id)}
                formulas={formulas}
              />
            </div>
          ))
        )}
      </div>

      {/* Add Rule Button */}
      <div className="mt-3 flex justify-center">
        <button
          onClick={handleAddRule}
          className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-md hover:border-blue-400 hover:bg-blue-50 transition-colors text-sm"
        >
          <Plus className="h-4 w-4" />
          Add Rule
        </button>
      </div>
    </div>
  );
};

export default FilterGroupEditor;
